import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "../axios";

const INTERVAL = 5000;

export default function Notices() {
  const [notices, setNotices]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [current, setCurrent]   = useState(0);
  const timerRef                = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("/notices?limit=10");
        setNotices(res.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % notices.length);
    }, INTERVAL);
  }, [notices.length]);

  useEffect(() => {
    if (notices.length > 1) startTimer();
    return () => clearInterval(timerRef.current);
  }, [notices.length, startTimer]);

  const goTo = (idx) => {
    setCurrent(idx);
    startTimer();
  };

  if (loading) return <div style={styles.center}>読み込み中...</div>;
  if (!notices.length) return <div style={styles.center}>お知らせはありません。</div>;

  const notice = notices[current];
  const isPinned = !!notice.pinned;

  const fmt = (str) => {
    const d = new Date(str);
    return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}:${String(d.getSeconds()).padStart(2,"0")}`;
  };

  return (
    <div style={styles.wrapper}>
      <h2 style={{ textAlign: "center", fontSize: "20px", fontWeight: "bold", marginBottom: "16px", color: "#065f46" }}>
        お知らせ
        {notices.length > 1 && (
          <span style={{ fontSize: "0.7rem", color: "#6b9e88", marginLeft: "8px", fontWeight: "400" }}>
            {current + 1} / {notices.length}
          </span>
        )}
      </h2>

      {/* カード本体 */}
      <div
        key={current}
        style={{
          ...styles.card,
          background:    isPinned ? "#fffbeb" : "#f0fdf4",
          borderLeft:    isPinned ? "4px solid #d97706" : "4px solid #059669",
          boxShadow:     isPinned
            ? "0 2px 10px rgba(217,119,6,0.12)"
            : "0 2px 10px rgba(4,120,87,0.08)",
        }}
        onMouseEnter={() => clearInterval(timerRef.current)}
        onMouseLeave={() => { if (notices.length > 1) startTimer(); }}
      >
        <div style={styles.cardTop}>
          {isPinned && <span style={styles.pinBadge}>📌 重要</span>}
          <span style={{ ...styles.dateBadge, marginLeft: "auto" }}>
            {fmt(notice.created_at)}
          </span>
        </div>
        <div style={{ ...styles.title, color: isPinned ? "#92400e" : "#064e3b" }}>
          {notice.title || "お知らせ"}
        </div>
        <div style={{ ...styles.content, color: isPinned ? "#b45309" : "#047857" }}>
          {notice.content}
        </div>
      </div>

      {/* ドットインジケーター */}
      {notices.length > 1 && (
        <div style={styles.dots}>
          {notices.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              style={{
                ...styles.dot,
                background: i === current
                  ? (isPinned ? "#d97706" : "#059669")
                  : (isPinned ? "rgba(217,119,6,0.25)" : "rgba(4,120,87,0.22)"),
                transform: i === current ? "scale(1.3)" : "scale(1)",
              }}
              aria-label={`お知らせ ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* プログレスバー */}
      {notices.length > 1 && (
        <div style={{ ...styles.progressTrack, background: isPinned ? "#fde68a" : "#d1fae5" }}>
          <div
            key={`${current}-progress`}
            style={{ ...styles.progressBar, background: isPinned ? "#d97706" : "#059669" }}
          />
        </div>
      )}

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateX(80px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  wrapper: {
    maxWidth: "960px",
    margin: "0 auto",
    padding: "4px",
  },
  center: { textAlign: "center" },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "12px",
  },
  headerIcon: { fontSize: "1.1rem" },
  headerText: {
    fontSize: "1rem",
    fontWeight: "700",
    color: "#064e3b",
    letterSpacing: "0.04em",
  },
  counter: {
    marginLeft: "auto",
    fontSize: "0.75rem",
    color: "#6b9e88",
    fontVariantNumeric: "tabular-nums",
  },
  card: {
    background: "#ffffff",
    borderLeft: "4px solid #059669",
    borderRadius: "10px",
    padding: "14px 18px",
    boxShadow: "0 2px 10px rgba(4,120,87,0.08)",
    animation: "fadeSlide 0.3s ease",
    minHeight: "90px",
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
  },
  pinBadge: {
    fontSize: "0.7rem",
    fontWeight: "700",
    background: "#fef3c7",
    color: "#92400e",
    padding: "2px 8px",
    borderRadius: "999px",
    border: "1px solid #fde68a",
  },
  dateBadge: {
    fontSize: "0.72rem",
    color: "#6b9e88",
  },
  title: {
    fontSize: "0.97rem",
    fontWeight: "700",
    marginBottom: "6px",
  },
  content: {
    fontSize: "0.875rem",
    color: "#047857",
    lineHeight: "1.65",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  dots: {
    display: "flex",
    justifyContent: "center",
    gap: "7px",
    marginTop: "14px",
  },
  dot: {
    width: "9px",
    height: "9px",
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    padding: 0,
    transition: "transform 0.2s, background 0.2s",
  },
  progressTrack: {
    height: "2px",
    borderRadius: "2px",
    marginTop: "10px",
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    borderRadius: "2px",
    animation: `progress ${INTERVAL}ms linear`,
  },
};
